<script setup lang="ts">

import { AppText } from '@shared/ui/typography'
import { AppTooltip } from '@shared/ui/tooltips'
import { AppTable, AppTd, AppTh, AppTr } from '@shared/ui/tables'
import { LinkTextPrimary } from '@shared/ui/links'

import { useTableDisciplineBalances } from '../model/table-discipline-balances.hook'

defineOptions({
  name: 'TableDisciplineBalances',
})

const COLUMNS: readonly { title: string, className: string }[] = [
  { title: 'Date and time', className: 'table-discipline-balances__date' },
  { title: 'Counter', className: 'table-discipline-balances__number' },
  { title: 'Balance', className: 'table-discipline-balances__number' },
  { title: 'Reason', className: '' },
  { title: 'Responsible', className: 'table-discipline-balances__responsible' },
  { title: 'Reference', className: '' },
  { title: 'Comment', className: 'table-discipline-balances__comment' },
]

const {
  rows,
  isLoading,
  hasBalances,
  meta,
  perPageOptions,
  setChangePage,
  setChangePerPage,
} = useTableDisciplineBalances()
</script>

<template>
  <div class="table-discipline-balances">
    <AppTable
      class="rounded-table"
      :meta="meta"
      :per-page-options="perPageOptions"
      :loading="isLoading"
      @change:page="setChangePage"
      @change:per-page="setChangePerPage"
    >
      <template #head>
        <AppTr>
          <AppTh
            v-for="column in COLUMNS"
            :key="column.title"
            :class="column.className"
          >
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              {{ column.title }}
            </AppText>
          </AppTh>
        </AppTr>
      </template>

      <template
        v-if="hasBalances"
        #body
      >
        <AppTr
          v-for="row in rows"
          :key="row.id"
        >
          <AppTd class="medium table-discipline-balances__date">
            <AppText name="body1">
              {{ row.createdAt }}
            </AppText>
          </AppTd>
          <AppTd class="medium">
            <AppText
              name="body1-medium"
              :color="row.counterColor"
            >
              {{ row.counter }}
            </AppText>
          </AppTd>
          <AppTd class="medium">
            <AppText name="body1">
              {{ row.balance }}
            </AppText>
          </AppTd>
          <AppTd class="medium">
            <AppText name="body1">
              {{ row.reason }}
            </AppText>
          </AppTd>
          <AppTd class="medium">
            <LinkTextPrimary
              v-if="row.responsible.url"
              :href="row.responsible.url"
              target="_blank"
            >
              {{ row.responsible.text }}
            </LinkTextPrimary>
            <AppText
              v-else
              name="body1"
              color="slate-500"
            >
              {{ row.responsible.text }}
            </AppText>
          </AppTd>
          <AppTd class="medium">
            <LinkTextPrimary
              v-if="row.reference.url"
              :href="row.reference.url"
              target="_blank"
            >
              {{ row.reference.text }}
            </LinkTextPrimary>
            <AppText
              v-else
              name="body1"
            >
              {{ row.reference.text }}
            </AppText>
          </AppTd>
          <AppTd class="medium table-discipline-balances__comment">
            <AppTooltip
              v-if="row.commentTooltip"
              :text="row.commentTooltip"
              placement="top"
            >
              <AppText name="body1">
                {{ row.comment }}
              </AppText>
            </AppTooltip>
            <AppText
              v-else
              name="body1"
            >
              {{ row.comment }}
            </AppText>
          </AppTd>
        </AppTr>
      </template>
    </AppTable>
  </div>
</template>

<style scoped lang="scss" src="./TableDisciplineBalances.scss" />
